import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppContext } from '../../../../../../../../app/Context';
import { AttendanceData } from '../../../../../../../../db/classes/types';
import { getValidAttendanceData } from '../../../../../../../../db/classes';
import Logger from '../../../../../../../../utils/logger';
import { SubmitHandler, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { FormValues } from './types';
import { validationSchema } from './validation';
import { useDrawerContext } from '../../../../../../../../components/Drawer/Context';

interface AttendanceCourseAndClassData {
  courseData: AttendanceData['inSession'][number]['courseData'];
  classData: AttendanceData['inSession'][number]['classData'][number];
}

export interface UseRecordAttendanceDrawerForm {
  onNext: (data: AttendanceCourseAndClassData) => Promise<void> | void;
}

const useRecordAttendanceDrawerForm = ({ onNext }: UseRecordAttendanceDrawerForm) => {
  const { onClose } = useDrawerContext();
  const { setError } = useAppContext();

  const formMethods = useForm<FormValues>({
    resolver: joiResolver(validationSchema),
    defaultValues: {
      selectedClassId: null,
    },
    mode: 'all',
  });

  const { setValue } = formMethods;

  const [isFetchingAttendanceData, setIsFetchingAttendanceData] = useState(false);
  const [inSessionAttendanceData, setInSessionAttendanceData] = useState<AttendanceData['inSession']>([]);
  const [nextScheduledAttendanceData, setNextScheduledAttendanceData] = useState<
    AttendanceData['nextScheduled']
  >([]);

  const attendanceDataDictionary = useRef<Dictionary<AttendanceCourseAndClassData>>({});

  const fetchAttendanceData = useCallback(async () => {
    setIsFetchingAttendanceData(true);

    try {
      const { inSession, nextScheduled } = await getValidAttendanceData();
      setInSessionAttendanceData(inSession);
      setNextScheduledAttendanceData(nextScheduled);
    } catch (error: any) {
      setError(error.message);
      Logger.error(error);
    } finally {
      setIsFetchingAttendanceData(false);
    }
  }, [setError]);

  useEffect(() => {
    fetchAttendanceData();
  }, [fetchAttendanceData]);

  const createAttendanceDataDictionary = useCallback(() => {
    if (isFetchingAttendanceData) return;

    const inSessionAttendanceDataDictionary = inSessionAttendanceData.reduce((acc, attendanceData) => {
      const { courseData, classData } = attendanceData;
      for (let cls of classData) {
        acc[cls.classId] = {
          courseData,
          classData: cls,
        };
      }
      return acc;
    }, {} as Dictionary<AttendanceCourseAndClassData>);

    attendanceDataDictionary.current = inSessionAttendanceDataDictionary;
  }, [isFetchingAttendanceData, inSessionAttendanceData]);

  useEffect(() => {
    createAttendanceDataDictionary();
  }, [createAttendanceDataDictionary]);

  const getSelectedAttendanceData = (selectedClassId: MongoId | null) => {
    if (!selectedClassId) return null;

    const selectedAttendanceData = attendanceDataDictionary.current[selectedClassId];
    return selectedAttendanceData ?? null;
  };

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async (data) => {
      const { selectedClassId } = data;

      let attedanceData = getSelectedAttendanceData(selectedClassId);
      if (!attedanceData) return;

      await onNext(attedanceData);
    },
    [onNext, inSessionAttendanceData]
  );

  useEffect(() => {
    /** select first class when there is only 1 class */
    if (inSessionAttendanceData.length !== 1) return;
    if (inSessionAttendanceData[0].classData.length !== 1) return;

    const firstClassId = inSessionAttendanceData[0].classData[0].classId;
    setValue('selectedClassId', firstClassId, { shouldValidate: true });
  }, [inSessionAttendanceData, setValue]);

  return {
    onClose,
    formMethods,
    onSubmit,
    isFetchingAttendanceData,
    inSessionAttendanceData,
    nextScheduledAttendanceData,
  };
};

export default useRecordAttendanceDrawerForm;
